/**
 * Edge AO / micro-bevel shader injection.
 *
 * Real machine-planed lumber has a fine softening at every edge — a
 * 0.5–2 mm chamfer that catches a tiny shadow under any directional
 * light. Our parametric geometry uses BoxGeometry with hard 90° edges,
 * so renders read as "CAD cubes" instead of "boards."
 *
 * We cannot bevel the geometry without abandoning the shared UNIT_BOX
 * (see geometry.ts — every addBox() reuses one BufferGeometry to keep
 * GPU memory bounded; per-mesh bevel would explode that). Instead we
 * fake the chamfer in the fragment shader: detect normal discontinuity
 * via screen-space derivatives and darken the pixel slightly. The
 * result reads as a soft bevel everywhere two faces meet, at the cost
 * of one `length(fwidth(normal))` per fragment.
 *
 * Stacks with `applyWoodVariation` — the two patches target different
 * shader chunks and don't collide.
 */
import type * as THREE from "three";

interface EdgeBevelParams {
	/**
	 * Maximum darkening at a perfectly sharp edge. 0.18 reads as a
	 * machined chamfer; >0.3 starts looking like ink-bleed cartoon
	 * outlines. Default: 0.18.
	 */
	strength?: number;
	/**
	 * How quickly the bevel falls off away from the edge. Higher =
	 * tighter shadow band. Default: 6.
	 */
	sharpness?: number;
}

const DEFAULT_PARAMS: Required<EdgeBevelParams> = {
	strength: 0.18,
	sharpness: 6,
};

type ShaderStage = "vertex" | "fragment";

function replaceShaderChunkOrThrow(
	shaderSource: string,
	chunk: string,
	replacement: string,
	stage: ShaderStage,
): string {
	if (!shaderSource.includes(chunk)) {
		throw new Error(
			`[edgeBevel] Missing ${stage} shader chunk "${chunk}". ` +
				"Three.js shader layout changed; update applyEdgeBevel injection points.",
		);
	}
	return shaderSource.replace(chunk, replacement);
}

/**
 * Apply edge-bevel AO to an existing MeshStandardMaterial.
 *
 * Wraps the material's existing `onBeforeCompile` so this stacks
 * cleanly on top of `applyWoodVariation`. The wood variation patch
 * targets `<color_fragment>` and `<roughnessmap_fragment>`; this patch
 * targets `<output_fragment>` — they do not overlap.
 */
export function applyEdgeBevel(
	material: THREE.MeshStandardMaterial,
	params: EdgeBevelParams = {},
): void {
	const p = { ...DEFAULT_PARAMS, ...params };
	const existingHook = material.onBeforeCompile;

	// Three.js shares compiled programs across materials by default,
	// keyed on material settings + customProgramCacheKey. The shader
	// source we emit varies with `strength` and `sharpness`, but the
	// renderer cannot see those closure values — without a custom
	// key it would happily reuse the first compiled program for ALL
	// materials with `applyEdgeBevel`, so a 0.22-strength trim would
	// silently render with whatever variant compiled first (e.g.
	// 0.14 deckboard). Chain the key onto any prior one so this
	// composes with applyWoodVariation's key.
	const existingCacheKey = material.customProgramCacheKey?.bind(material);
	const ourKey = `edge-bevel:${p.strength.toFixed(4)}:${p.sharpness.toFixed(2)}`;
	material.customProgramCacheKey = () =>
		existingCacheKey ? `${existingCacheKey()}|${ourKey}` : ourKey;

	material.onBeforeCompile = (shader, renderer) => {
		// Stack on top of any prior onBeforeCompile (woodVariation etc).
		existingHook?.(shader, renderer);

		// Pass a world-space normal through to the fragment shader so we
		// can run fwidth() on it. We piggy-back on the worldpos_vertex
		// chunk which is already injected for variation patches.
		// vWoodWorldNormal is also declared by woodVariation; we
		// declare a separate name to keep the patches independent.
		shader.vertexShader = replaceShaderChunkOrThrow(
			shader.vertexShader,
			"#include <common>",
			`#include <common>
varying vec3 vBevelWorldNormal;`,
			"vertex",
		);
		shader.vertexShader = replaceShaderChunkOrThrow(
			shader.vertexShader,
			"#include <worldpos_vertex>",
			`#include <worldpos_vertex>
vBevelWorldNormal = normalize(mat3(modelMatrix) * normal);`,
			"vertex",
		);

		shader.fragmentShader = replaceShaderChunkOrThrow(
			shader.fragmentShader,
			"#include <common>",
			`#include <common>
varying vec3 vBevelWorldNormal;`,
			"fragment",
		);

		// Inject right before tonemap so the bevel modulates the final
		// lit color rather than the unlit diffuse. We use derivatives of
		// the world-space normal — `length(fwidth(n))` spikes at face
		// boundaries because the normal changes direction abruptly
		// between two faces of a box, while it stays ~0 across a flat
		// face. We saturate-and-pow to get a tight dark band.
		shader.fragmentShader = replaceShaderChunkOrThrow(
			shader.fragmentShader,
			"#include <tonemapping_fragment>",
			`{
  // Per-fragment normal derivative magnitude — large at edges,
  // ~0 across flat faces. fwidth = |dFdx| + |dFdy|.
  vec3 nDeriv = fwidth(vBevelWorldNormal);
  float edge = clamp(length(nDeriv) * ${p.sharpness.toFixed(2)}, 0.0, 1.0);
  // Apply a soft curve so the very-edge darkens hardest but the
  // falloff is smooth across a few pixels (matches a real micro-
  // chamfer that catches one or two pixels of shadow at most).
  float bevel = pow(edge, 0.65);
  gl_FragColor.rgb *= 1.0 - bevel * ${p.strength.toFixed(4)};
}
#include <tonemapping_fragment>`,
			"fragment",
		);
	};

	material.needsUpdate = true;
}
